import React, { useState, useEffect } from 'react';
import useOperationalStore, { getTimeHealth, fmtAge, fmtDur } from '../platform/store/useOperationalStore';
import { urgencyTier } from '../platform/engine/priorityScore';
import FullscreenBtn from '../components/FullscreenBtn';
import './TimelineView.css';

// Color per micro-status (stage breakdown segments + legend)
const STAGE_COLORS = {
  yard:          '#6366f1',
  triage:        '#8b5cf6',
  waiting:       '#0ea5e9',
  approval:      '#f59e0b',
  'parts-hold':  '#ef4444',
  wip:           '#22c55e',
  'road-test':   '#06b6d4',
  qc:            '#f59e0b',
  done:          '#3b82f6',
};

const STATUS_LABELS = {
  yard:          'Intake',
  triage:        'Triage',
  waiting:       'Diag',
  approval:      'Approval',
  'parts-hold':  'Parts',
  wip:           'Repair',
  'road-test':   'Road Test',
  qc:            'QC',
  done:          'Ready',
};

// Urgency tier — same ranking as PriorityView, kept visually consistent.
const TIER_COLORS = ['var(--tt-red)', 'var(--tt-gold)', 'var(--tt-gold)', 'var(--tt-green)'];
const TIER_LABELS = ['Critical', 'High', 'Medium', 'On Track'];

function fmtTime(ms) {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ─── Stage legend ─────────────────────────────────────────────────────────────

function StageLegend() {
  const stages = Object.entries(STATUS_LABELS);
  return (
    <div className="tl-legend">
      {stages.map(([status, label]) => (
        <div key={status} className="tl-legend-item">
          <span className="tl-legend-dot" style={{ background: STAGE_COLORS[status] }} />
          <span className="tl-legend-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Stage breakdown segments (relative to THIS truck's own elapsed time) ──────
// Each row is normalized to its own total elapsed time, so a 2-hour-old RO and
// a 2-month-old RO both render a full-width bar — what differs is the stage
// breakdown inside it, not a shared absolute scale that crushes everything.

function buildSegments(truck, now) {
  const history = truck.statusHistory || [];
  if (!history.length) {
    if (!truck.startTime) return [];
    return [{
      pct: 100,
      color: STAGE_COLORS[truck.status] || '#94a3b8',
      label: STATUS_LABELS[truck.status] || truck.status,
      ms: now - truck.startTime * 1000,
      isCurrent: true,
    }];
  }
  const start   = history[0].timeEntered;
  const totalMs = Math.max(1, now - start);
  return history
    .map((entry, i) => {
      const segStart = entry.timeEntered;
      const segEnd   = history[i + 1]?.timeEntered ?? now;
      const ms       = Math.max(0, segEnd - segStart);
      return {
        pct:       (ms / totalMs) * 100,
        color:     STAGE_COLORS[entry.status] || '#94a3b8',
        label:     STATUS_LABELS[entry.status] || entry.status,
        ms,
        isCurrent: i === history.length - 1,
      };
    })
    .filter(s => s.pct > 0.3);
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function TlRow({ truck, rank, now, onClick, delayMs }) {
  const currentColor = STAGE_COLORS[truck.status] || '#94a3b8';
  const statusLabel  = STATUS_LABELS[truck.status] || truck.status;
  const tierColor    = TIER_COLORS[rank] || 'var(--tt-green)';
  const segments     = buildSegments(truck, now);
  const health       = getTimeHealth(truck, now); // null if no estimate set

  let badge = null;
  if (health) {
    const pctUsed = Math.round(health.pct * 100);
    badge = (
      <span className={`tl-est-badge tl-est-badge--${health.status}`}>
        {pctUsed}% of est{health.status === 'red' ? ' — over' : ''}
      </span>
    );
  } else if (truck.promisedTime) {
    const remaining = truck.promisedTime - now;
    badge = remaining < 0
      ? <span className="tl-est-badge tl-est-badge--red">overdue {fmtDur(-remaining)}</span>
      : <span className="tl-est-badge tl-est-badge--neutral">due in {fmtDur(remaining)}</span>;
  } else {
    badge = <span className="tl-no-est">no estimate set</span>;
  }

  return (
    <div
      className="tl-row"
      style={{ '--tier-color': tierColor, '--row-delay': `${delayMs}ms` }}
      onClick={onClick}
    >
      <span className="tl-tier-stripe" />

      {/* Label */}
      <div className="tl-label">
        <div className="tl-label-top">
          <span className="tl-ro">{truck.repairOrder}</span>
          {truck.isHardDown && <span className="tl-fd">FD</span>}
        </div>
        <span className="tl-company">
          {[truck.truckNumber, truck.companyName?.slice(0, 18)].filter(Boolean).join(' — ') || 'No company on file'}
        </span>
        <span className="tl-status-pill" style={{ color: currentColor, borderColor: currentColor + '55', background: currentColor + '18' }}>
          {statusLabel}
        </span>
      </div>

      {/* Proportional stage-breakdown bar — always fills the track */}
      <div className="tl-track">
        {segments.length > 0 ? (
          segments.map((seg, i) => (
            <div
              key={i}
              className={`tl-seg${seg.isCurrent ? ' tl-seg--current' : ''}`}
              style={{ width: `${seg.pct}%`, background: seg.color }}
              title={`${seg.label} — ${fmtDur(seg.ms)}`}
            />
          ))
        ) : (
          <div className="tl-seg-empty">No activity recorded yet</div>
        )}
      </div>

      {/* Age + estimate/promise status */}
      <div className="tl-meta">
        <span className="tl-age">{fmtAge(truck.startTime, now)} open</span>
        {badge}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TimelineView() {
  const trucks           = useOperationalStore(s => s.trucks);
  const setSelectedTruck = useOperationalStore(s => s.setSelectedTruckId);
  const [now, setNow]       = useState(Date.now());
  const [filterRank, setFilterRank] = useState(null); // null = all tiers

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const active = trucks.filter(t => !['completed', 'voided'].includes(t.status));

  if (!active.length) {
    return (
      <div className="tl-root">
        <div className="vtb">
          <div className="vtb-left"><span className="vtb-stat-lbl">No active jobs</span></div>
          <div className="vtb-right"><FullscreenBtn /></div>
        </div>
        <div className="tl-empty">No active jobs</div>
      </div>
    );
  }

  // Bucket + sort: most urgent tier first, then oldest-open first within tier.
  const enriched = active.map(truck => ({ truck, rank: urgencyTier(truck) }));
  enriched.sort((a, b) => a.rank - b.rank || (a.truck.startTime || 0) - (b.truck.startTime || 0));

  const counts = [0, 0, 0, 0];
  enriched.forEach(e => counts[e.rank]++);

  const visible = filterRank === null ? enriched : enriched.filter(e => e.rank === filterRank);

  return (
    <div className="tl-root">

      <div className="vtb">
        <div className="vtb-left">
          <div className="vtb-stat"><span className="vtb-stat-val">{active.length}</span><span className="vtb-stat-lbl">Active ROs</span></div>
          <div className="vtb-divider" />
          <span className="tl-now-label">Now: {fmtTime(now)}</span>
        </div>
        <div className="vtb-right"><FullscreenBtn /></div>
      </div>

      {/* Interactive tier filter */}
      <div className="tl-filters">
        {TIER_LABELS.map((label, rank) => (
          <button
            key={rank}
            className={`tl-filter-chip${filterRank === rank ? ' tl-filter-chip--active' : ''}`}
            style={{ '--chip-color': TIER_COLORS[rank] }}
            onClick={() => setFilterRank(filterRank === rank ? null : rank)}
          >
            <span className="tl-filter-dot" />
            {label}
            <span className="tl-filter-count">{counts[rank]}</span>
          </button>
        ))}
        {filterRank !== null && (
          <button className="tl-filter-clear" onClick={() => setFilterRank(null)}>Show all</button>
        )}
      </div>

      <div className="tl-body">
        <div className="tl-rows">
          {visible.length === 0 && <div className="tl-empty">Nothing in this tier</div>}
          {visible.map(({ truck, rank }, i) => (
            <TlRow
              key={truck.id}
              truck={truck}
              rank={rank}
              now={now}
              delayMs={Math.min(i, 12) * 30}
              onClick={() => setSelectedTruck(truck.id)}
            />
          ))}
        </div>

        <StageLegend />
      </div>
    </div>
  );
}
